import React, { useContext, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { POWERED_BY_FOOTER_HEIGHT_PX } from 'config/constants';
import FilterTimePeriod from 'components/molecules/Filter/FilterTimePeriod';
import CampaignFilterTimePeriod from 'components/molecules/Filter/CampaignFilterTimePeriod';
import FilterV2Header from 'components/molecules/FilterV2/FilterV2Header';
import FilterV2Footer from 'components/molecules/FilterV2/FilterV2Footer';
import FilterV2AttributeList from 'components/molecules/FilterV2/FilterV2AttributeList';
import FilterV2AppliedFilters from 'components/molecules/FilterV2/FilterV2AppliedFilters';
import FilterV2TierRow from 'components/molecules/FilterV2/FilterV2TierRow';
import FilterV2PresetSection from 'components/molecules/FilterV2/FilterV2PresetSection';
import useFilterV2Reset from 'hooks/Filter/useFilterV2Reset';

const globalFilterContainerId = 'parentGlobalFilterContainer';

interface Props {
    /** Left edge of the fixed panel. Navbar V2 passes the rail width; V1 leaves it flush-left. */
    leftOffset?: string;
    /** Top edge of the fixed panel. Unset keeps V1's navbar-height margin-top utility class. */
    topOffset?: string;
    /** Navbar V2's MainContainer owns `#main-container` margin-left, so it opts out here. */
    ownsMainContainerMargin?: boolean;
}

const getShellPlacement = (leftOffset?: string, topOffset?: string) => {
    if (!leftOffset && !topOffset) return { className: 'main-screen-margin-top-no-margin-top', style: {} };
    return { className: '', style: { top: topOffset, left: leftOffset } };
};

export default function FilterV2({ leftOffset, topOffset, ownsMainContainerMargin = true }: Props) {
    const {
        allDimAndAttributeType,
        allCheckedAttribute,
        loadingForAllCheckAttribute,
        timePeriodSelection,
        setTimePeriodSelection,
        timePeriodList,
        setTimePeriodList,
        timeSelectionLabel,
        setTimeSelectionLabel,
        latestDate,
        isGlobalFilterDisabled,
        isGlobalFilterCollapsed,
        screenFilterConfig,
        hideGlobalTimeFilterComponent,
        showTierFilter,
    } = useContext(FilterContextData);

    const [attributeTypeExpandClose, setAttributeTypeExpandClose] = useState('');
    const [pane, setPane] = useState<'filters' | 'applied'>('filters');
    const [isPresetsSaveSelectionOpen, setIsPresetsSaveSelectionOpen] = useState(false);

    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const resetFilter = useFilterV2Reset(setAttributeTypeExpandClose);
    const isFiltersPane = pane === 'filters';
    const placement = getShellPlacement(leftOffset, topOffset);

    // V2 has no expand column, so a collapsed panel leaves no gutter behind
    useEffect(() => {
        if (!ownsMainContainerMargin) return undefined;
        const mainContainer = document.getElementById('main-container');
        if (!mainContainer) return undefined;

        if (isGlobalFilterCollapsed) {
            mainContainer.style.marginLeft = '0px';
        } else {
            mainContainer.style.removeProperty('margin-left');
        }

        return () => {
            mainContainer.style.removeProperty('margin-left');
        };
    }, [isGlobalFilterCollapsed, ownsMainContainerMargin]);

    // Selection cards are antd popovers portalled to the body, so a click outside both the
    // panel and any popover closes whichever row is open.
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            const target = event.target as HTMLElement;
            if (document.getElementById(globalFilterContainerId)?.contains(target)) return;
            if (target?.closest?.('.ant-popover')) return;
            setAttributeTypeExpandClose('');
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const timePeriodProps = {
        variant: 'v2' as const,
        onClose: () => setAttributeTypeExpandClose(''),
        latestDate,
        timePeriod: timePeriodList,
        attributeTypeExpandClose,
        setAttributeTypeExpandClose,
        timePeriodSelection,
        setTimePeriodSelection,
        setTimePeriodList,
        setTimeSelectionLabel,
        timeSelectionLabel,
    };

    return (
        <div
            className={`flex z-[51] fixed top-0 ${placement.className} ${
                isGlobalFilterDisabled ? 'cursor-not-allowed' : ''
            }`}
            style={{ bottom: POWERED_BY_FOOTER_HEIGHT_PX, ...placement.style }}
        >
            <div
                className={`bg-white global-filter-v2-width pt-4 relative z-20 border-r border-filterV2Border flex flex-col h-full ${
                    isGlobalFilterDisabled ? 'pointer-events-none' : ''
                }`}
                style={
                    isGlobalFilterCollapsed
                        ? { width: 0, minWidth: 0, overflow: 'hidden', paddingTop: 0, transition: 'width 500ms ease' }
                        : { transition: 'width 500ms ease' }
                }
            >
                <FilterV2Header
                    title={isFiltersPane ? 'Global Filter' : 'Applied Filters'}
                    direction={isFiltersPane ? 'right' : 'left'}
                    onArrowClick={() => setPane(isFiltersPane ? 'applied' : 'filters')}
                />
                <FilterV2PresetSection
                    isFiltersPane={isFiltersPane}
                    isSaveOpen={isPresetsSaveSelectionOpen}
                    setIsSaveOpen={setIsPresetsSaveSelectionOpen}
                />
                {loadingForAllCheckAttribute ? (
                    <div className="flex-1 flex justify-center items-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto border-t border-filterV2Border" id={globalFilterContainerId}>
                        {isFiltersPane ? (
                            <>
                                {!hideGlobalTimeFilterComponent &&
                                    (commonConfig.showCampignCalender ? (
                                        <CampaignFilterTimePeriod {...timePeriodProps} />
                                    ) : (
                                        <FilterTimePeriod {...timePeriodProps} />
                                    ))}
                                {showTierFilter && (
                                    <FilterV2TierRow
                                        attributeTypeExpandClose={attributeTypeExpandClose}
                                        setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                                    />
                                )}
                                <FilterV2AttributeList
                                    globalFilterContainerId={globalFilterContainerId}
                                    allDimAndAttributeType={allDimAndAttributeType}
                                    allCheckedAttribute={allCheckedAttribute}
                                    attributeTypeExpandClose={attributeTypeExpandClose}
                                    setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                                    onShowMore={() => setPane('applied')}
                                    screenFilterConfig={screenFilterConfig}
                                    uppercaseFilterLabels={globalFilterConfig?.uppercaseFilterLabels}
                                />
                            </>
                        ) : (
                            <FilterV2AppliedFilters
                                timeSelectionLabel={timeSelectionLabel}
                                screenFilterConfig={screenFilterConfig}
                                setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                                uppercaseFilterLabels={globalFilterConfig?.uppercaseFilterLabels}
                            />
                        )}
                    </div>
                )}
                <FilterV2Footer
                    isFiltersPane={isFiltersPane}
                    setAttributeTypeExpandClose={setAttributeTypeExpandClose}
                    onReset={resetFilter}
                    onSaveSelection={() => setIsPresetsSaveSelectionOpen(true)}
                />
            </div>
        </div>
    );
}
